import React from "react";
import "@testing-library/jest-dom";
import { render, screen, fireEvent } from "@testing-library/react";

import { VoiceInputButton } from "./VoiceInputButton";

const mockUseVoiceRecording = jest.fn();
const mockStart = jest.fn();
const mockStop = jest.fn();
const mockCancel = jest.fn();

jest.mock("./useVoiceRecording", () => ({
  useVoiceRecording: (args: unknown) => mockUseVoiceRecording(args),
}));

jest.mock("./voiceInputUtils", () => {
  const actual = jest.requireActual("./voiceInputUtils");
  return {
    __esModule: true,
    ...actual,
    isVoiceInputSupported: () => true,
  };
});

function setHookReturn(
  overrides: Partial<{
    state: string;
    error: { code: string; message: string } | null;
    partialTranscript: string;
  }> = {},
) {
  mockUseVoiceRecording.mockReturnValue({
    state: "idle",
    error: null,
    partialTranscript: "",
    start: mockStart,
    stop: mockStop,
    cancel: mockCancel,
    ...overrides,
  });
}

describe("VoiceInputButton", () => {
  beforeEach(() => {
    mockStart.mockReset();
    mockStop.mockReset();
    mockCancel.mockReset();
    mockUseVoiceRecording.mockReset();
    setHookReturn();
    window.localStorage.clear();
  });

  function renderButton(
    overrides: Partial<React.ComponentProps<typeof VoiceInputButton>> = {},
  ) {
    return render(
      <VoiceInputButton
        workspaceId="ws-test"
        onTranscriptUpdate={() => undefined}
        onAutoSubmit={() => undefined}
        userId="user-1"
        {...overrides}
      />,
    );
  }

  it("renders the mic button in idle state with correct aria-label", () => {
    renderButton();
    const btn = screen.getByTestId("voice-input-button");
    expect(btn).toBeInTheDocument();
    expect(btn).toHaveAttribute(
      "aria-label",
      expect.stringMatching(/start voice input/i),
    );
    expect(btn).toHaveAttribute("aria-pressed", "false");
  });

  it("opens the privacy modal on first click and persists consent on accept", () => {
    renderButton();
    fireEvent.click(screen.getByTestId("voice-input-button"));
    expect(
      screen.getByText(/your microphone audio is streamed/i),
    ).toBeInTheDocument();
    expect(mockStart).not.toHaveBeenCalled();

    fireEvent.click(screen.getByText(/i understand/i));
    expect(mockStart).toHaveBeenCalledTimes(1);
    expect(window.localStorage.getItem("voice_input_consent_v1:user-1")).toBe(
      "true",
    );
  });

  it("does not show the modal on subsequent clicks after consent", () => {
    window.localStorage.setItem("voice_input_consent_v1:user-1", "true");
    renderButton();
    fireEvent.click(screen.getByTestId("voice-input-button"));
    expect(mockStart).toHaveBeenCalledTimes(1);
    expect(screen.queryByText(/i understand/i)).toBeNull();
  });

  it("flips aria-label and aria-pressed while recording", () => {
    setHookReturn({ state: "recording" });
    renderButton();
    const btn = screen.getByTestId("voice-input-button");
    // Recording aria-label must tell the truth: click stops AND sends.
    expect(btn).toHaveAttribute(
      "aria-label",
      expect.stringMatching(/stop and send/i),
    );
    expect(btn).toHaveAttribute("aria-pressed", "true");
  });

  it("stops and sends (not cancels) when the mic is clicked while recording", () => {
    setHookReturn({ state: "recording" });
    renderButton();
    fireEvent.click(screen.getByTestId("voice-input-button"));
    expect(mockStop).toHaveBeenCalledTimes(1);
    expect(mockCancel).not.toHaveBeenCalled();
    expect(mockStart).not.toHaveBeenCalled();
  });

  it("triggers the toggle on Ctrl+Shift+M from any focus point", () => {
    window.localStorage.setItem("voice_input_consent_v1:user-1", "true");
    renderButton();
    fireEvent.keyDown(window, { key: "m", ctrlKey: true, shiftKey: true });
    expect(mockStart).toHaveBeenCalledTimes(1);
  });

  it("cancels (discards) recording on Escape, not stop-and-send", () => {
    setHookReturn({ state: "recording" });
    renderButton();
    fireEvent.keyDown(window, { key: "Escape" });
    expect(mockCancel).toHaveBeenCalledTimes(1);
    expect(mockStop).not.toHaveBeenCalled();
  });

  it("never renders a no-speech notice element (#125: silent timeout)", () => {
    setHookReturn({ state: "recording" });
    renderButton();
    expect(screen.queryByTestId("voice-input-notice")).toBeNull();
  });

  it("renders an error message when the hook surfaces a voice input error", () => {
    setHookReturn({
      error: {
        code: "permission-denied",
        message: "Microphone access was blocked.",
      },
    });
    renderButton();
    expect(screen.getByRole("alert")).toHaveTextContent(
      /microphone access was blocked/i,
    );
  });

  it("disables the button when disabled prop is set", () => {
    renderButton({ disabled: true });
    expect(screen.getByTestId("voice-input-button")).toBeDisabled();
  });
});
